import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';

interface WarningDialogProps {
  header: string;
  isVisible: boolean;
  onHide: () => any;
  addIcon?: boolean;
  content: string;
  quoteName?: string;
  dangerousContent?: string;
  smallLetters?: string;
  notInformation?: string;
  notInformationVariant?: 'yellow' | 'blue';
  /** When set, the confirm button stays disabled until the user types this exact string (trims input). */
  requiredTypeingToApproved?: string | null;
  onConfirm?: () => void | Promise<void>;
  confirmLabel?: string;
  confirmLoading?: boolean;
  cancelLabel?: string;
  showButtons?: boolean;
  disableApproveButton?: boolean;
  approveButtonToolTip?: string;
}

export const WarningDialog: React.FC<WarningDialogProps> = ({
  header,
  isVisible,
  onHide,
  addIcon = false,
  content,
  quoteName,
  dangerousContent,
  smallLetters,
  notInformation,
  notInformationVariant = 'yellow',
  onConfirm,
  confirmLabel = 'אישור',
  confirmLoading = false,
  cancelLabel = 'ביטול',
  showButtons = true,
  disableApproveButton = false,
  approveButtonToolTip,
  requiredTypeingToApproved = null,
}) => {
  const [confirmationInput, setConfirmationInput] = useState('');

  useEffect(() => {
    if (isVisible) {
      setConfirmationInput('');
    }
  }, [isVisible]);

  const requiresTyping =
    requiredTypeingToApproved != null && requiredTypeingToApproved !== '';

  const typingBlocksApprove =
    requiresTyping && confirmationInput.trim() !== requiredTypeingToApproved;

  const handleConfirm = async () => {
    if (typingBlocksApprove) {
      return;
    }
    if (onConfirm) {
      await onConfirm();
    }
  };

  const dialogFooter = showButtons ? (
    <div>
      <Button
        label={cancelLabel}
        icon="pi pi-times"
        className="p-button-outlined p-button-secondary"
        onClick={onHide}
        disabled={confirmLoading}
      />
      {onConfirm && (
        <Button
          label={confirmLabel}
          icon="pi pi-check"
          className="p-button-danger"
          onClick={handleConfirm}
          loading={confirmLoading}
          disabled={
            confirmLoading || disableApproveButton || typingBlocksApprove
          }
          tooltip={
            disableApproveButton
              ? approveButtonToolTip
              : typingBlocksApprove
                ? 'יש להקליד את מילת האישור המדויקת'
                : undefined
          }
          tooltipOptions={{ position: 'top' }}
          autoFocus={!requiresTyping}
        />
      )}
    </div>
  ) : undefined;

  return (
    <Dialog
      header={header}
      visible={isVisible}
      onHide={() => {
        if (!confirmLoading) {
          onHide();
        }
      }}
      style={{
        width: '500px',
        backgroundColor: 'white',
        padding: '8px',
        borderRadius: '8px',
      }}
      footer={dialogFooter}
      closable={!confirmLoading}
      modal
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <div style={{ textAlign: addIcon ? 'center' : 'right' }}>
          {addIcon && (
            <i
              className="pi pi-exclamation-triangle"
              style={{
                fontSize: '3rem',
                color: '#f59e0b',
                marginBottom: '16px',
                display: 'block',
              }}
            />
          )}

          <p
            style={{
              fontSize: addIcon ? '1.1rem' : '1rem',
              color: '#495057',
              fontWeight: 'bold',
              marginBottom: quoteName
                ? '8px'
                : dangerousContent || smallLetters
                  ? '16px'
                  : '0',
            }}
          >
            {content}
          </p>

          {quoteName && (
            <p
              style={{
                fontSize: '1.2rem',
                fontWeight: 'bold',
                color: '#1f2937',
                marginBottom: dangerousContent || smallLetters ? '16px' : '0',
              }}
            >
              "{quoteName}"
            </p>
          )}

          {dangerousContent && (
            <p
              style={{
                fontSize: '0.9rem',
                color: '#dc2626',
                marginBottom: smallLetters
                  ? '16px'
                  : notInformation
                    ? '24px'
                    : '0',
              }}
            >
              <i className="pi pi-info-circle" style={{ marginLeft: '4px' }} />
              {dangerousContent}
            </p>
          )}

          {smallLetters && (
            <p
              style={{
                fontSize: '0.9rem',
                color: '#6b7280',
                marginBottom: notInformation ? '24px' : '0',
              }}
            >
              {smallLetters}
            </p>
          )}
        </div>

        {notInformation && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '16px',
              backgroundColor:
                notInformationVariant === 'blue' ? '#DBEAFE' : '#FEF3C7',
              padding: '16px',
              borderRadius: '8px',
              border:
                notInformationVariant === 'blue'
                  ? '1px solid #3B82F6'
                  : '1px solid #ffc107',
            }}
          >
            <i
              className="pi pi-info-circle"
              style={{
                fontSize: '1.5rem',
                color: notInformationVariant === 'blue' ? '#1D4ED8' : '#856404',
                marginTop: '2px',
              }}
            />
            <p
              style={{
                margin: 0,
                fontSize: '0.95rem',
                color: notInformationVariant === 'blue' ? '#1D4ED8' : '#856404',
                lineHeight: '1.5',
              }}
            >
              {notInformation}
            </p>
          </div>
        )}

        {requiresTyping && (
          <div style={{ textAlign: 'right' }}>
            <label
              htmlFor="warning-dialog-confirmation-input"
              style={{
                display: 'block',
                fontSize: '0.9rem',
                color: '#374151',
                marginBottom: '8px',
              }}
            >
              הקלד את המילה{' '}
              <strong style={{ fontFamily: 'monospace' }}>
                {requiredTypeingToApproved}
              </strong>{' '}
              לאישור:
            </label>
            <InputText
              id="warning-dialog-confirmation-input"
              value={confirmationInput}
              onChange={(e) => setConfirmationInput(e.target.value)}
              autoComplete="off"
              autoFocus
              style={{ width: '100%', direction: 'ltr', textAlign: 'left' }}
              disabled={confirmLoading}
            />
          </div>
        )}
      </div>
    </Dialog>
  );
};
